import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { Scatterplot } from '@/components/scatterplot';
import { countryIdToName } from '@/utils/country-id-to-name';
import { t, Trans } from '@lingui/macro';
import { useState } from 'react';

export function UnderPressureCh2(props: { data: UnderPressureData }) {
  const { data } = props;

  const [highlightedIds, setHighlightedIds] = useState<CountryIsoCode[]>();

  const chartData = (
    Object.entries(data.more_pressure_country_breakdown) as [CountryIsoCode, AgeGroupBreakdown][]
  ).map(([key, value]) => ({
    id: key,
    label: countryIdToName(key),
    ...value,
  }));

  const handleStepEnter = (evt: OnStepEnterExitArgs<CountryIsoCode[] | undefined>) => {
    setHighlightedIds(evt.data);
  };

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <Scatterplot
          data={chartData}
          highlightedIds={highlightedIds}
          getXValue={(d) => d.older}
          getYValue={(d) => d.younger}
          topAxisLabel={t({
            id: 'under-pressure.chapter-2.chart-top-label',
            message:
              'more 15-24 year-olds say children face more pressure to succeed than their parents did',
          })}
          bottomAxisLabel={t({
            id: 'under-pressure.chapter-2.chart-bottom-label',
            message:
              'more 40+ year-olds say children face more pressure to succeed than their parents did',
          })}
        />
      </ScrollGraphic>

      <ScrollSteps onStepEnter={handleStepEnter} offset={0.5} hasGraphic>
        <ScrollStep>
          <StoryText>
            <Trans id="under-pressure.chapter-2.text-1">
              This is true in nearly <PrimaryAnnotatedType>all the countries</PrimaryAnnotatedType>{' '}
              we surveyed.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={chartData.filter((el) => el.younger > 0.5).map((el) => el.id)}>
          <StoryText>
            <Trans id="under-pressure.chapter-2.text-2">
              In {data.more_pressure_country_counts.n_where_younger_agree} of{' '}
              {data.more_pressure_country_counts.n} countries, the majority of young people believe
              children today face <AnnotatedType>more pressure to succeed.</AnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="under-pressure.chapter-2.text-3">
              But it's not just youth feeling sorry for themselves&hellip;
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep data={chartData.filter((el) => el.older > 0.5).map((el) => el.id)}>
          <StoryText>
            <Trans id="under-pressure.chapter-2.text-4">
              Majorities of <AnnotatedType>older people</AnnotatedType> in{' '}
              {data.more_pressure_country_counts.n_where_older_agree} of{' '}
              {data.more_pressure_country_counts.n} countries agree that kids today face more
              pressure to succeed than in the past.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
